import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import { FormInput } from "@/components/Base/Form";
import Button from "@/components/Base/Button";
import Table from "@/components/Base/Table";
import { Menu } from "@/components/Base/Headless";
import PageLoadingOverlay from "@/components/PageLoadingOverlay";

type ReceiptLine = { productId: string; quantity: number; product?: { id: string; name: string; sku: string | null } };
type Receipt = {
  id: string;
  receivedAt: string;
  invoiceNo: string | null;
  stockInType?: string | null;
  distributor: { id: string; name: string; code: string; region?: string } | null;
  lines: ReceiptLine[];
};
type Region = { id: string; name: string };
type ProductRow = { id: string; sku: string; name: string };
type ProductType = { id: string; typeName: string };

const STORAGE_KEY_FILTER = "stockledger_stockInTable_filter";
const STORAGE_KEY_SORT = "stockledger_stockInTable_sort";
const STORAGE_KEY_REGION = "stockledger_stockInTable_region";
const PRODUCT_TYPE_ORDER = ["Devices", "Refills", "Accessories"];

function loadSavedFilter(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_FILTER);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((x: unknown) => typeof x === "string") ? parsed : [];
  } catch {
    return [];
  }
}
function loadSavedSortOrder(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SORT);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((x: unknown) => typeof x === "string") ? parsed : [];
  } catch {
    return [];
  }
}

function loadSavedRegion(): string {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_REGION);
    if (!raw || typeof raw !== "string") return "";
    return raw.trim();
  } catch {
    return "";
  }
}

const COL_NO_PX = 44;
const COL_SKU_PX = 90;
const COL_PRODUCT_PX = 200;
const COL_RECEIPT_STOCKIN_PX = 92;
const COL_RECEIPT_EXCHANGE_PX = 90;
const COL_MONTH_OPENING_PX = 95;
const COL_TOTAL_PX = 95;

function colStyle(w: number): React.CSSProperties {
  return { width: w, minWidth: w, maxWidth: w };
}

const formatInt = (n: number) => n.toLocaleString("en-US");
const formatIntOrDash = (n: number) => (n === 0 ? "-" : formatInt(n));

function formatDateInvoice(receivedAt: string, invoiceNo: string | null): string {
  const d = new Date(receivedAt);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  const dateStr = `${dd}/${mm}/${yy}`;
  return invoiceNo && invoiceNo.trim() ? `${dateStr} - ${invoiceNo.trim()}` : dateStr;
}

export default function StockInListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyIdFromUrl = searchParams.get("companyId");

  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [productIdToType, setProductIdToType] = useState<Record<string, string>>({});
  const [allProducts, setAllProducts] = useState<ProductRow[]>([]);
  const [openingByMonth, setOpeningByMonth] = useState<Record<string, Record<string, number>>>({});
  const [selectedProductTypeFilters, setSelectedProductTypeFilters] = useState<string[]>(loadSavedFilter);
  const [productTypeCustomOrder, setProductTypeCustomOrder] = useState<string[]>(loadSavedSortOrder);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedMonth, setSelectedMonth] = useState<string>(
    () => `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`
  );
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegionId, setSelectedRegionId] = useState<string>(loadSavedRegion);

  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";

  const selectedMonthLabel = new Date(selectedMonth + "-01").toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const MONTH_NAMES = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  useEffect(() => {
    if (selectedMonth) setPickerYear(parseInt(selectedMonth.slice(0, 4), 10));
  }, [selectedMonth]);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/regions?pageSize=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json().catch(() => ({}));
        const list = Array.isArray(json?.data) ? json.data : [];
        setRegions(list.filter((r: Region) => r.id && r.name));
      } catch {
        setRegions([]);
      }
    })();
  }, [apiUrl]);

  useEffect(() => {
    if (!selectedRegionId.trim()) {
      setReceipts([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const monthQuery = `?month=${selectedMonth}&regionId=${encodeURIComponent(selectedRegionId)}`;
    const token = getToken();
    (async () => {
      try {
        setLoading(true);
        const [tableRes, typesRes, productsRes] = await Promise.all([
          fetch(`${apiUrl}/stock-in/month-table${monthQuery}`, {
            signal: controller.signal,
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${apiUrl}/product-types?pageSize=200`, {
            signal: controller.signal,
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${apiUrl}/products?pageSize=500`, {
            signal: controller.signal,
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const tableJson = await tableRes.json().catch(() => ({}));
        const typesJson = await typesRes.json().catch(() => ({}));
        const productsJson = await productsRes.json().catch(() => ({}));
        const monthReceipts = tableRes.ok && Array.isArray(tableJson?.data) ? tableJson.data : [];
        setReceipts(monthReceipts);
        setProductTypes(Array.isArray(typesJson?.data) ? typesJson.data : []);
        const prods = Array.isArray(productsJson?.data) ? productsJson.data : [];
        const idToType: Record<string, string> = {};
        const mappedProducts: ProductRow[] = [];
        prods.forEach((p: { id: string; type?: string; sku?: string | null; name?: string | null }) => {
          if (p.id && (p.type || "").trim()) idToType[p.id] = String(p.type).trim();
          if (p.id) {
            mappedProducts.push({
              id: p.id,
              sku: p.sku ?? "",
              name: p.name ?? "",
            });
          }
        });
        setProductIdToType(idToType);
        setAllProducts(mappedProducts);

      } catch (e) {
        if ((e as Error)?.name !== "AbortError") {
          setReceipts([]);
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [apiUrl, selectedMonth, selectedRegionId]);

  const filteredReceipts = useMemo(() => {
    if (!searchQuery.trim()) return receipts;
    const q = searchQuery.trim().toLowerCase();
    return receipts.filter((r) => {
      const batchNo = (r as unknown as { batchNo?: string }).batchNo ?? "";
      const invoiceNo = r.invoiceNo ?? "";
      return batchNo.toLowerCase().includes(q) || invoiceNo.toLowerCase().includes(q);
    });
  }, [receipts, searchQuery]);

  const products = useMemo(() => {
    // Keep product ordering consistent with Products page (displayOrder from /products)
    const idsInReceipts = new Set<string>();
    filteredReceipts.forEach((r) => {
      (r.lines || []).forEach((line) => {
        if (line.productId) idsInReceipts.add(line.productId);
      });
    });
    let list = allProducts.filter((p) => idsInReceipts.has(p.id));
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((p) => (p.sku?.toLowerCase().includes(q) || p.name?.toLowerCase().includes(q)));
    }
    return list;
  }, [allProducts, filteredReceipts, searchQuery]);

  const productsByType = useMemo(() => {
    const map = new Map<string, { label: string; products: ProductRow[] }>();
    products.forEach((p) => {
      const typeName = (productIdToType[p.id] || "").trim() || "Other";
      if (!map.has(typeName)) map.set(typeName, { label: typeName, products: [] });
      map.get(typeName)!.products.push(p);
    });
    return map;
  }, [products, productIdToType]);

  const orderedProductTypes = useMemo(() => {
    const entries = Array.from(productsByType.entries());
    const ordered: [string, { label: string; products: ProductRow[] }][] = [];
    for (const name of PRODUCT_TYPE_ORDER) {
      const found = entries.find(([, v]) => v.label.trim().toLowerCase() === name.toLowerCase());
      if (found) ordered.push(found);
    }
    for (const e of entries) {
      if (!PRODUCT_TYPE_ORDER.some((n) => n.toLowerCase() === e[1].label.trim().toLowerCase())) ordered.push(e);
    }
    return ordered;
  }, [productsByType]);

  const filteredAndSortedProductTypes = useMemo(() => {
    let list = orderedProductTypes;
    if (selectedProductTypeFilters.length > 0) {
      const set = new Set(selectedProductTypeFilters.map((s) => s.trim().toLowerCase()));
      list = list.filter(([, v]) => set.has(v.label.trim().toLowerCase()));
    }
    if (productTypeCustomOrder.length > 0) {
      const orderMap = new Map(productTypeCustomOrder.map((label, i) => [label.trim().toLowerCase(), i]));
      list = [...list].sort((a, b) => {
        const ia = orderMap.get(a[1].label.trim().toLowerCase()) ?? 1e9;
        const ib = orderMap.get(b[1].label.trim().toLowerCase()) ?? 1e9;
        return ia - ib;
      });
    }
    return list;
  }, [orderedProductTypes, selectedProductTypeFilters, productTypeCustomOrder]);

  const sortMenuOrder = useMemo(() => {
    if (productTypeCustomOrder.length > 0) {
      const inCustom = productTypeCustomOrder.filter((label) =>
        orderedProductTypes.some(([, v]) => v.label.trim().toLowerCase() === label.trim().toLowerCase())
      );
      const rest = orderedProductTypes
        .filter(([, v]) => !productTypeCustomOrder.some((c) => c.trim().toLowerCase() === v.label.trim().toLowerCase()))
        .map(([, v]) => v.label);
      return [...inCustom, ...rest];
    }
    return orderedProductTypes.map(([, v]) => v.label);
  }, [orderedProductTypes, productTypeCustomOrder]);

  const moveSortOrder = useCallback((index: number, direction: "up" | "down") => {
    setProductTypeCustomOrder((prev) => {
      const next = [...sortMenuOrder];
      const j = direction === "up" ? index - 1 : index + 1;
      if (j < 0 || j >= next.length) return prev.length > 0 ? prev : [];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  }, [sortMenuOrder]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_FILTER, JSON.stringify(selectedProductTypeFilters));
    } catch {
      // ignore
    }
  }, [selectedProductTypeFilters]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SORT, JSON.stringify(productTypeCustomOrder));
    } catch {
      // ignore
    }
  }, [productTypeCustomOrder]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_REGION, selectedRegionId);
    } catch {
      // ignore
    }
  }, [selectedRegionId]);

  const inByProductReceipt = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    filteredReceipts.forEach((r) => {
      (r.lines || []).forEach((line) => {
        if (!line.productId || line.quantity == null || line.quantity <= 0) return;
        if (!map.has(line.productId)) map.set(line.productId, new Map());
        const recMap = map.get(line.productId)!;
        const prev = recMap.get(r.id) ?? 0;
        recMap.set(r.id, prev + line.quantity);
      });
    });
    return map;
  }, [filteredReceipts]);

  const exchangeByProductReceipt = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    filteredReceipts.forEach((r) => {
      (r.lines || []).forEach((line) => {
        if (!line.productId || line.quantity == null || line.quantity >= 0) return;
        if (!map.has(line.productId)) map.set(line.productId, new Map());
        const recMap = map.get(line.productId)!;
        const prev = recMap.get(r.id) ?? 0;
        recMap.set(r.id, prev + Math.abs(line.quantity));
      });
    });
    return map;
  }, [filteredReceipts]);

  const distributorInByProductMonth = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    filteredReceipts.forEach((r) => {
      const d = new Date(r.receivedAt);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      (r.lines || []).forEach((line) => {
        if (!line.productId || line.quantity == null || line.quantity <= 0) return;
        if (!map.has(line.productId)) map.set(line.productId, new Map());
        const monthMap = map.get(line.productId)!;
        monthMap.set(monthKey, (monthMap.get(monthKey) ?? 0) + line.quantity);
      });
    });
    return map;
  }, [filteredReceipts]);

  const exchangeByProductMonth = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    filteredReceipts.forEach((r) => {
      const d = new Date(r.receivedAt);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      (r.lines || []).forEach((line) => {
        if (!line.productId || line.quantity == null || line.quantity >= 0) return;
        if (!map.has(line.productId)) map.set(line.productId, new Map());
        const monthMap = map.get(line.productId)!;
        monthMap.set(monthKey, (monthMap.get(monthKey) ?? 0) + Math.abs(line.quantity));
      });
    });
    return map;
  }, [filteredReceipts]);

  const totalByProduct = useMemo(() => {
    const monthKeys = new Set<string>();
    filteredReceipts.forEach((r) => {
      const d = new Date(r.receivedAt);
      monthKeys.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    });

    const map = new Map<string, number>();
    products.forEach((p) => {
      let sum = 0;
      monthKeys.forEach((monthKey) => {
        const opening = openingByMonth[monthKey]?.[p.id] ?? 0;
        const inPlus = distributorInByProductMonth.get(p.id)?.get(monthKey) ?? 0;
        const ex = exchangeByProductMonth.get(p.id)?.get(monthKey) ?? 0;
        sum += opening + inPlus - ex;
      });
      map.set(p.id, sum);
    });
    return map;
  }, [products, filteredReceipts, openingByMonth, distributorInByProductMonth, exchangeByProductMonth]);

  const monthGroups = useMemo(() => {
    const groups = new Map<string, Receipt[]>();
    filteredReceipts.forEach((r) => {
      const d = new Date(r.receivedAt);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const key = `${y}-${m}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(r);
    });
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, receipts]) => ({
        key,
        monthLabel: new Date(`${key}-01`).toLocaleString("default", { month: "long", year: "numeric" }),
        receipts: [...receipts].sort((a, b) => new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime()),
      }));
  }, [filteredReceipts]);

  useEffect(() => {
    const controller = new AbortController();
    const token = getToken();
    (async () => {
      try {
        if (monthGroups.length === 0) {
          setOpeningByMonth({});
          return;
        }
        const rows = await Promise.all(
          monthGroups.map(async (g) => {
            const asOf = `${g.key}-01T00:00:00.000Z`;
            const res = await fetch(
              `${apiUrl}/stock-in/opening-balances?asOf=${encodeURIComponent(asOf)}`,
              {
                signal: controller.signal,
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            const json = await res.json().catch(() => []);
            const map: Record<string, number> = {};
            if (res.ok && Array.isArray(json)) {
              json.forEach((row: { productId: string; balance: number }) => {
                if (row && row.productId) map[row.productId] = row.balance ?? 0;
              });
            }
            return { monthKey: g.key, map };
          })
        );
        const byMonth: Record<string, Record<string, number>> = {};
        rows.forEach((r) => {
          byMonth[r.monthKey] = r.map;
        });
        setOpeningByMonth(byMonth);
      } catch (e) {
        if ((e as Error)?.name !== "AbortError") setOpeningByMonth({});
      }
    })();
    return () => controller.abort();
  }, [apiUrl, monthGroups]);

  const addUrl = companyIdFromUrl ? `/stock-in/add?companyId=${companyIdFromUrl}` : "/stock-in/add";
  const editUrl = (id: string) => (companyIdFromUrl ? `/stock-in/${id}/edit?companyId=${companyIdFromUrl}` : `/stock-in/${id}/edit`);

  const tableWidth =
    COL_NO_PX +
    COL_SKU_PX +
    COL_PRODUCT_PX +
    monthGroups.reduce(
      (sum, g) => sum + COL_MONTH_OPENING_PX + g.receipts.length * (COL_RECEIPT_STOCKIN_PX + COL_RECEIPT_EXCHANGE_PX),
      0
    ) +
    COL_TOTAL_PX;

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 w-full">
          <PageLoadingOverlay show={loading && Boolean(selectedRegionId.trim())} label="Loading stock in…" />
          <div className="col-span-12">
            <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">
                Stock IN
              </div>
              <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                <Button
                  variant="primary"
                  className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                  onClick={() => navigate(addUrl)}
                >
                  <Lucide icon="PenLine" className="stroke-[1.3] w-4 h-4 mr-2" />
                  Add New Stock IN
                </Button>
              </div>
            </div>

            <div className="mt-3.5">
              <div className="flex flex-col box box--stacked">
                <div className="relative z-[100] flex flex-col p-5 sm:items-center sm:flex-row gap-y-2 gap-x-3">
                  <div className="flex items-center gap-2">
                    <Menu className="relative inline-block">
                      <Menu.Button className="relative inline-flex items-center w-full min-w-[160px] sm:w-[200px] pl-9 pr-8 py-2 rounded-[0.5rem] border border-slate-300/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 text-slate-700 dark:text-slate-300 text-left text-sm hover:bg-slate-50 dark:hover:bg-darkmode-700 transition-colors">
                        <Lucide icon="Calendar" className="absolute left-0 inset-y-0 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500 pointer-events-none" />
                        <span className="truncate">{selectedMonthLabel}</span>
                        <Lucide icon="ChevronDown" className="absolute right-0 w-4 h-4 my-auto mr-2 stroke-[1.3] text-slate-400 pointer-events-none" />
                      </Menu.Button>
                      <Menu.Items className="absolute left-0 mt-1 z-[100] w-56 rounded-[0.6rem] border border-slate-200/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 shadow-lg py-2" style={{ zIndex: 9999 }}>
                        <div className="flex items-center justify-between px-3 py-2 gap-2">
                          <button type="button" onClick={(e) => { e.preventDefault(); setPickerYear((y) => y - 1); }} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-darkmode-600">
                            <Lucide icon="ChevronLeft" className="w-4 h-4" />
                          </button>
                          <span className="text-sm font-medium tabular-nums">{pickerYear}</span>
                          <button type="button" onClick={(e) => { e.preventDefault(); setPickerYear((y) => y + 1); }} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-darkmode-600">
                            <Lucide icon="ChevronRight" className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-1 px-2 pb-2">
                          {MONTH_NAMES.map((name, i) => {
                            const value = `${pickerYear}-${String(i + 1).padStart(2, "0")}`;
                            const isSelected = selectedMonth === value;
                            return (
                              <Menu.Item key={value}>
                                {({ close }) => (
                                  <button type="button" onClick={() => { setSelectedMonth(value); close(); }} className={`py-2 px-2 rounded text-xs font-medium transition-colors ${isSelected ? "bg-primary text-white dark:bg-primary" : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-darkmode-600"}`}>
                                    {name}
                                  </button>
                                )}
                              </Menu.Item>
                            );
                          })}
                        </div>
                      </Menu.Items>
                    </Menu>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600 dark:text-slate-400 shrink-0">Region</span>
                    <Menu className="relative inline-block">
                      <Menu.Button className="relative inline-flex items-center w-full min-w-[180px] sm:w-[220px] pl-9 pr-8 py-2 rounded-[0.5rem] border border-slate-300/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 text-slate-700 dark:text-slate-300 text-left text-sm hover:bg-slate-50 dark:hover:bg-darkmode-700 transition-colors">
                        <Lucide icon="MapPin" className="absolute left-0 inset-y-0 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500 pointer-events-none" />
                        <span className="truncate">{selectedRegionId ? (regions.find((r) => r.id === selectedRegionId)?.name ?? "Select region...") : "Select region..."}</span>
                        <Lucide icon="ChevronDown" className="absolute right-0 w-4 h-4 my-auto mr-2 stroke-[1.3] text-slate-400 pointer-events-none" />
                      </Menu.Button>
                      <Menu.Items className="absolute left-0 mt-1 z-[100] w-56 rounded-[0.6rem] border border-slate-200/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 shadow-lg py-2 max-h-64 overflow-y-auto" style={{ zIndex: 9999 }}>
                        {regions.map((r) => (
                          <Menu.Item key={r.id}>
                            {({ close }) => (
                              <button
                                type="button"
                                onClick={() => { setSelectedRegionId(r.id); close(); }}
                                className={`w-full text-left px-3 py-2 text-sm ${selectedRegionId === r.id ? "bg-primary text-white dark:bg-primary" : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-darkmode-600"}`}
                              >
                                {r.name}
                              </button>
                            )}
                          </Menu.Item>
                        ))}
                        {regions.length === 0 && (
                          <div className="px-3 py-2 text-sm text-slate-500">No regions available</div>
                        )}
                      </Menu.Items>
                    </Menu>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
                    <Menu className="relative inline-block">
                      <Menu.Button className="relative inline-flex items-center min-w-[90px] w-auto pl-9 pr-3 py-2 rounded-[0.5rem] border border-slate-300/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 text-slate-700 dark:text-slate-300 text-left text-sm whitespace-nowrap hover:bg-slate-50 dark:hover:bg-darkmode-700">
                        <Lucide icon="Filter" className="absolute left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500" />
                        Filter
                        {selectedProductTypeFilters.length > 0 && <span className="ml-1 text-xs">({selectedProductTypeFilters.length})</span>}
                      </Menu.Button>
                      <Menu.Items className="w-64 z-[100]" style={{ zIndex: 9999 }}>
                        <div className="p-4">
                          <div className="text-sm font-medium mb-2">Product Type</div>
                          {orderedProductTypes.length > 0 && (
                            <button
                              type="button"
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline mb-2"
                              onClick={() => {
                                const all = orderedProductTypes.every(([, v]) =>
                                  selectedProductTypeFilters.some((s) => s.trim().toLowerCase() === v.label.trim().toLowerCase())
                                );
                                setSelectedProductTypeFilters(all ? [] : orderedProductTypes.map(([, v]) => v.label));
                              }}
                            >
                              {orderedProductTypes.every(([, v]) =>
                                selectedProductTypeFilters.some((s) => s.trim().toLowerCase() === v.label.trim().toLowerCase())
                              ) ? "Deselect all" : "Select all"}
                            </button>
                          )}
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {orderedProductTypes.map(([, v]) => (
                              <label key={v.label} className="flex items-center cursor-pointer gap-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input rounded border-slate-300"
                                  checked={selectedProductTypeFilters.some((s) => s.trim().toLowerCase() === v.label.trim().toLowerCase())}
                                  onChange={(e) => {
                                    if (e.target.checked) setSelectedProductTypeFilters([...selectedProductTypeFilters, v.label]);
                                    else setSelectedProductTypeFilters(selectedProductTypeFilters.filter((s) => s.trim().toLowerCase() !== v.label.trim().toLowerCase()));
                                  }}
                                />
                                <span className="text-xs">{v.label}</span>
                              </label>
                            ))}
                          </div>
                          {selectedProductTypeFilters.length > 0 && (
                            <button type="button" className="w-full mt-3 px-3 py-1.5 text-xs bg-slate-200 dark:bg-darkmode-700 rounded" onClick={() => setSelectedProductTypeFilters([])}>Clear Filters</button>
                          )}
                        </div>
                      </Menu.Items>
                    </Menu>
                    <Menu className="relative inline-block">
                      <Menu.Button className="relative inline-flex items-center min-w-[90px] w-auto pl-9 pr-3 py-2 rounded-[0.5rem] border border-slate-300/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 text-slate-700 dark:text-slate-300 text-left text-sm whitespace-nowrap hover:bg-slate-50 dark:hover:bg-darkmode-700">
                        <Lucide icon="ArrowUpDown" className="absolute left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500" />
                        Sort
                        {productTypeCustomOrder.length > 0 && <span className="ml-1 text-xs">(Custom)</span>}
                      </Menu.Button>
                      <Menu.Items className="w-64 z-[100]" style={{ zIndex: 9999 }}>
                        <div className="p-4">
                          <div className="text-sm font-medium mb-2">Display order</div>
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {sortMenuOrder.map((label, index) => (
                              <div key={label} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-slate-100 dark:hover:bg-darkmode-600">
                                <span className="text-xs truncate flex-1">{index + 1}. {label}</span>
                                <div className="flex shrink-0 gap-0.5">
                                  <button type="button" className="p-1 rounded hover:bg-slate-200 disabled:opacity-40 disabled:pointer-events-none" title="Move up" disabled={index === 0} onClick={() => moveSortOrder(index, "up")}>
                                    <Lucide icon="ChevronUp" className="w-3.5 h-3.5" />
                                  </button>
                                  <button type="button" className="p-1 rounded hover:bg-slate-200 disabled:opacity-40 disabled:pointer-events-none" title="Move down" disabled={index === sortMenuOrder.length - 1} onClick={() => moveSortOrder(index, "down")}>
                                    <Lucide icon="ChevronDown" className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                          {productTypeCustomOrder.length > 0 && (
                            <button type="button" className="w-full mt-3 px-3 py-1.5 text-xs bg-slate-200 dark:bg-darkmode-700 rounded" onClick={() => setProductTypeCustomOrder([])}>Reset to default</button>
                          )}
                        </div>
                      </Menu.Items>
                    </Menu>
                    <div className="relative">
                      <Lucide icon="Search" className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500" />
                      <FormInput type="text" placeholder="Search batch no..." className="pl-9 sm:w-56 rounded-[0.5rem]" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
                    </div>
                    <Button variant="outline-secondary" type="button" className="w-full sm:w-auto shrink-0" onClick={() => setSearchQuery(searchInput.trim())}>
                      <Lucide icon="Search" className="stroke-[1.3] w-4 h-4 mr-2" />
                      Search
                    </Button>
                  </div>
                </div>

                {!selectedRegionId.trim() ? (
                  <div className="p-10 flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
                    Please select a Region to view Stock IN data.
                  </div>
                ) : (
                  <div className="relative z-10 px-5 pb-5 space-y-8">
                    {filteredAndSortedProductTypes.length === 0 ? (
                      <div className="overflow-auto">
                        <Table className="border-b border-slate-200/60 text-xs" style={{ tableLayout: "fixed", width: tableWidth, minWidth: tableWidth }}>
                          <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                            <Table.Tr>
                              <Table.Td style={colStyle(COL_NO_PX)} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r-2 border-slate-200 dark:border-darkmode-400">NO</Table.Td>
                              <Table.Td style={colStyle(COL_SKU_PX)} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[44px] z-30 border-r border-slate-200 dark:border-darkmode-400">SKU ITEM</Table.Td>
                              <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[134px] z-30 border-r border-slate-200 dark:border-darkmode-400">PRODUCT NAME</Table.Td>
                              {monthGroups.map((g) => (
                                <Table.Td
                                  key={g.key}
                                  colSpan={1 + g.receipts.length * 2}
                                  className="py-2 px-2 font-medium text-center bg-primary/10 dark:bg-primary/10 border-r border-slate-200 dark:border-darkmode-400"
                                >
                                  {g.monthLabel}
                                </Table.Td>
                              ))}
                              <Table.Td style={colStyle(COL_TOTAL_PX)} className="py-2 px-2 font-medium text-center bg-success/10 dark:bg-success/10 border-r border-slate-200 dark:border-darkmode-400" />
                            </Table.Tr>
                            {filteredReceipts.length > 0 && (
                              <Table.Tr>
                                <Table.Td style={colStyle(COL_NO_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r-2 border-slate-200 dark:border-darkmode-400" />
                                <Table.Td style={colStyle(COL_SKU_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[44px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                                <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[134px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                                {monthGroups.map((g) => [
                                  <Table.Td
                                    key={`open-head-${g.key}`}
                                    style={colStyle(COL_MONTH_OPENING_PX)}
                                    className="py-1 px-1 font-medium text-center bg-slate-100 dark:bg-darkmode-700 text-[11px] border-r border-slate-200 dark:border-darkmode-400"
                                  >
                                  </Table.Td>,
                                  ...g.receipts.map((r) => (
                                    <Table.Td
                                      key={`rec-head-${r.id}`}
                                      colSpan={2}
                                      className="py-1 px-1 font-medium text-center bg-slate-50/70 dark:bg-darkmode-700/30 whitespace-normal break-words leading-tight text-[11px] border-r border-slate-200 dark:border-darkmode-400"
                                    >
                                      <div className="text-[10px] text-slate-500 mt-0.5">{r.distributor?.name ?? "—"}</div>
                                      <button type="button" onClick={() => navigate(editUrl(r.id))} className="text-primary hover:underline cursor-pointer">
                                        {formatDateInvoice(r.receivedAt, r.invoiceNo)}
                                      </button>
                                    </Table.Td>
                                  )),
                                ])}
                                <Table.Td style={colStyle(COL_TOTAL_PX)} className="py-1.5 px-2 bg-success/10 dark:bg-success/10 border-r border-slate-200 dark:border-darkmode-400" />
                              </Table.Tr>
                            )}
                            <Table.Tr>
                              <Table.Td style={colStyle(COL_NO_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r-2 border-slate-200 dark:border-darkmode-400" />
                              <Table.Td style={colStyle(COL_SKU_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[44px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                              <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[134px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                              {monthGroups.map((g) => [
                                <Table.Td key={`open-${g.key}`} style={colStyle(COL_MONTH_OPENING_PX)} className="py-1.5 px-1 font-medium text-center bg-slate-100 dark:bg-darkmode-700 text-[11px] border-r border-slate-200 dark:border-darkmode-400">OPENING STOCK</Table.Td>,
                                ...g.receipts.flatMap((r) => [
                                  <Table.Td key={`in-${r.id}`} style={colStyle(COL_RECEIPT_STOCKIN_PX)} className="py-1.5 px-1 font-medium text-center bg-success/10 dark:bg-success/10 text-[11px] border-r border-slate-200 dark:border-darkmode-400">IN (+)</Table.Td>,
                                  <Table.Td key={`ex-${r.id}`} style={colStyle(COL_RECEIPT_EXCHANGE_PX)} className="py-1.5 px-1 font-medium text-center bg-warning/10 dark:bg-warning/10 text-[11px] border-r border-slate-200 dark:border-darkmode-400">EXCHANGE (-)</Table.Td>,
                                ]),
                              ])}
                              <Table.Td style={colStyle(COL_TOTAL_PX)} className="py-1.5 px-2 font-medium text-center bg-success/10 dark:bg-success/10 leading-tight text-[11px] border-r border-slate-200 dark:border-darkmode-400">TOTAL STOCK IN</Table.Td>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            <Table.Tr>
                              <Table.Td colSpan={4 + monthGroups.reduce((sum, g) => sum + 1 + g.receipts.length * 2, 0)} className="py-8 text-center text-slate-500 dark:text-slate-400 border-dashed dark:bg-darkmode-600">
                                No Stock IN records for this month. Click &quot;Add New Stock IN&quot; to create one.
                              </Table.Td>
                            </Table.Tr>
                          </Table.Tbody>
                        </Table>
                      </div>
                    ) : (
                      filteredAndSortedProductTypes.map(([, { label, products: typeProducts }]) => {
                        const typeProductIds = new Set(typeProducts.map((p) => p.id));
                        const receiptsForType = filteredReceipts.filter((r) =>
                          (r.lines || []).some((line) => line.productId && typeProductIds.has(line.productId))
                        );
                        const monthGroupsForType = (() => {
                          const groups = new Map<string, Receipt[]>();
                          receiptsForType.forEach((r) => {
                            const d = new Date(r.receivedAt);
                            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                            if (!groups.has(key)) groups.set(key, []);
                            groups.get(key)!.push(r);
                          });
                          return Array.from(groups.entries())
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([key, recs]) => ({
                              key,
                              monthLabel: new Date(`${key}-01`).toLocaleString("default", { month: "long", year: "numeric" }),
                              receipts: [...recs].sort((a, b) => new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime()),
                            }));
                        })();
                        const typeDistributorByReceipt = receiptsForType.map((r) =>
                          typeProducts.reduce((sum, p) => {
                            const qty = inByProductReceipt.get(p.id)?.get(r.id) ?? 0;
                            return sum + qty;
                          }, 0)
                        );
                        const typeExchangeByReceipt = receiptsForType.map((r) =>
                          typeProducts.reduce((sum, p) => {
                            const qty = exchangeByProductReceipt.get(p.id)?.get(r.id) ?? 0;
                            return sum + qty;
                          }, 0)
                        );
                        const typeGrandTotal = typeProducts.reduce((sum, p) => sum + (totalByProduct.get(p.id) ?? 0), 0);
                        return (
                          <div key={label} className="overflow-auto rounded-lg border border-slate-200/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800">
                            <div className="px-4 py-2.5 border-b border-slate-200/60 dark:border-darkmode-400 bg-slate-50 dark:bg-darkmode-700 font-semibold text-slate-700 dark:text-slate-300 text-sm">
                              {label}
                            </div>
                            <Table className="border-b-0 text-xs" style={{ tableLayout: "fixed", width: tableWidth, minWidth: tableWidth }}>
                              <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                                <Table.Tr>
                                  <Table.Td style={colStyle(COL_NO_PX)} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r-2 border-slate-200 dark:border-darkmode-400">NO</Table.Td>
                                  <Table.Td style={colStyle(COL_SKU_PX)} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[44px] z-30 border-r border-slate-200 dark:border-darkmode-400">SKU ITEM</Table.Td>
                                  <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[134px] z-30 border-r border-slate-200 dark:border-darkmode-400">PRODUCT NAME</Table.Td>
                                  {monthGroupsForType.map((g) => (
                                    <Table.Td
                                      key={g.key}
                                      colSpan={1 + g.receipts.length * 2}
                                      className="py-2 px-2 font-medium text-center bg-primary/10 dark:bg-primary/10 border-r border-slate-200 dark:border-darkmode-400"
                                    >
                                      {g.monthLabel}
                                    </Table.Td>
                                  ))}
                                  <Table.Td style={colStyle(COL_TOTAL_PX)} className="py-2 px-2 font-medium text-center bg-success/10 dark:bg-success/10 border-r border-slate-200 dark:border-darkmode-400" />
                                </Table.Tr>
                                {receiptsForType.length > 0 && (
                                  <Table.Tr>
                                    <Table.Td style={colStyle(COL_NO_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r-2 border-slate-200 dark:border-darkmode-400" />
                                    <Table.Td style={colStyle(COL_SKU_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[44px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                                    <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[134px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                                    {monthGroupsForType.map((g) => [
                                      <Table.Td
                                        key={`open-head-${g.key}`}
                                        style={colStyle(COL_MONTH_OPENING_PX)}
                                        className="py-1 px-1 font-medium text-center bg-slate-100 dark:bg-darkmode-700 text-[11px] border-r border-slate-200 dark:border-darkmode-400"
                                      >
                                      </Table.Td>,
                                      ...g.receipts.map((r) => (
                                        <Table.Td
                                          key={`rec-head-${r.id}`}
                                          colSpan={2}
                                          className="py-1 px-1 font-medium text-center bg-slate-50/70 dark:bg-darkmode-700/30 whitespace-normal break-words leading-tight text-[11px] border-r border-slate-200 dark:border-darkmode-400"
                                        >
                                          <div className="text-[10px] text-slate-500 mt-0.5">{r.distributor?.name ?? "—"}</div>
                                          <button type="button" onClick={() => navigate(editUrl(r.id))} className="text-primary hover:underline cursor-pointer">
                                            {formatDateInvoice(r.receivedAt, r.invoiceNo)}
                                          </button>
                                        </Table.Td>
                                      )),
                                    ])}
                                    <Table.Td style={colStyle(COL_TOTAL_PX)} className="py-1.5 px-2 bg-success/10 dark:bg-success/10 border-r border-slate-200 dark:border-darkmode-400" />
                                  </Table.Tr>
                                )}
                                <Table.Tr>
                                  <Table.Td style={colStyle(COL_NO_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r-2 border-slate-200 dark:border-darkmode-400" />
                                  <Table.Td style={colStyle(COL_SKU_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[44px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                                  <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[134px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                                  {monthGroupsForType.map((g) => [
                                    <Table.Td key={`open-${g.key}`} style={colStyle(COL_MONTH_OPENING_PX)} className="py-1.5 px-1 font-medium text-center bg-slate-100 dark:bg-darkmode-700 text-[11px] border-r border-slate-200 dark:border-darkmode-400">OPENING STOCK</Table.Td>,
                                    ...g.receipts.flatMap((r) => [
                                      <Table.Td key={`in-${r.id}`} style={colStyle(COL_RECEIPT_STOCKIN_PX)} className="py-1.5 px-1 font-medium text-center bg-success/10 dark:bg-success/10 text-[11px] border-r border-slate-200 dark:border-darkmode-400">IN (+)</Table.Td>,
                                      <Table.Td key={`ex-${r.id}`} style={colStyle(COL_RECEIPT_EXCHANGE_PX)} className="py-1.5 px-1 font-medium text-center bg-warning/10 dark:bg-warning/10 text-[11px] border-r border-slate-200 dark:border-darkmode-400">EXCHANGE (-)</Table.Td>,
                                    ]),
                                  ])}
                                  <Table.Td style={colStyle(COL_TOTAL_PX)} className="py-1.5 px-2 font-medium text-center bg-success/10 dark:bg-success/10 leading-tight text-[11px] border-r border-slate-200 dark:border-darkmode-400">TOTAL STOCK IN</Table.Td>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {typeProducts.map((p, idx) => (
                                  <Table.Tr key={p.id} className="[&_td]:border-b [&_td]:border-slate-200/60">
                                    <Table.Td style={colStyle(COL_NO_PX)} className="py-2 px-2 bg-slate-50 dark:bg-darkmode-700 tabular-nums sticky left-0 z-20 border-r-2 border-slate-200 dark:border-darkmode-400">{idx + 1}</Table.Td>
                                    <Table.Td style={colStyle(COL_SKU_PX)} className="py-2 px-2 bg-slate-50 dark:bg-darkmode-700 break-words sticky left-[44px] z-20 border-r border-slate-200 dark:border-darkmode-400">{p.sku || "—"}</Table.Td>
                                    <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-2 px-2 bg-slate-50 dark:bg-darkmode-700 break-words sticky left-[134px] z-20 border-r border-slate-200 dark:border-darkmode-400">{p.name || "—"}</Table.Td>
                                    {monthGroupsForType.map((g) => [
                                      <Table.Td key={`open-val-${p.id}-${g.key}`} style={colStyle(COL_MONTH_OPENING_PX)} className="py-2 px-2 text-center tabular-nums whitespace-nowrap bg-slate-100 dark:bg-darkmode-700 text-[11px] border-r border-slate-200 dark:border-darkmode-400">
                                        {formatIntOrDash(openingByMonth[g.key]?.[p.id] ?? 0)}
                                      </Table.Td>,
                                      ...g.receipts.flatMap((r) => {
                                        const distributorQty = inByProductReceipt.get(p.id)?.get(r.id) ?? 0;
                                        const exchangeQty = exchangeByProductReceipt.get(p.id)?.get(r.id) ?? 0;
                                        return [
                                          <Table.Td key={`in-val-${p.id}-${r.id}`} style={colStyle(COL_RECEIPT_STOCKIN_PX)} className="py-2 px-2 text-center tabular-nums whitespace-nowrap bg-success/10 dark:bg-success/10 text-[11px] border-r border-slate-200 dark:border-darkmode-400">
                                            {formatIntOrDash(distributorQty)}
                                          </Table.Td>,
                                          <Table.Td key={`ex-val-${p.id}-${r.id}`} style={colStyle(COL_RECEIPT_EXCHANGE_PX)} className="py-2 px-2 text-center tabular-nums whitespace-nowrap bg-warning/10 dark:bg-warning/10 text-[11px] border-r border-slate-200 dark:border-darkmode-400">
                                            {formatIntOrDash(exchangeQty)}
                                          </Table.Td>,
                                        ];
                                      }),
                                    ])}
                                    <Table.Td style={colStyle(COL_TOTAL_PX)} className="py-2 px-2 text-center tabular-nums whitespace-nowrap bg-success/10 dark:bg-success/10 text-[11px] font-medium border-r border-slate-200 dark:border-darkmode-400">{formatIntOrDash(totalByProduct.get(p.id) ?? 0)}</Table.Td>
                                  </Table.Tr>
                                ))}
                                <Table.Tr className="[&_td]:border-b-0 [&_td]:border-t-2 [&_td]:border-t-slate-400 dark:[&_td]:border-t-darkmode-400 [&_td]:border-slate-200/60 bg-white dark:bg-darkmode-600 font-medium">
                                  <Table.Td style={colStyle(COL_NO_PX)} className="py-2 px-2 bg-white dark:bg-darkmode-600 sticky left-0 z-20 border-r-2 border-slate-200 dark:border-darkmode-400" />
                                  <Table.Td style={colStyle(COL_SKU_PX)} className="py-2 px-2 bg-white dark:bg-darkmode-600 sticky left-[44px] z-20 border-r border-slate-200 dark:border-darkmode-400" />
                                  <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-2 px-2 bg-white dark:bg-darkmode-600 sticky left-[134px] z-20 border-r border-slate-200 dark:border-darkmode-400" />
                                  {monthGroupsForType.map((g) => [
                                    <Table.Td key={`open-total-${g.key}`} style={colStyle(COL_MONTH_OPENING_PX)} className="py-2 px-2 text-center bg-white dark:bg-darkmode-600 text-[11px] border-r border-slate-200 dark:border-darkmode-400">—</Table.Td>,
                                    ...g.receipts.flatMap((r) => {
                                      const i = receiptsForType.findIndex((x) => x.id === r.id);
                                      return [
                                        <Table.Td key={`in-total-${r.id}`} style={colStyle(COL_RECEIPT_STOCKIN_PX)} className="py-2 px-2 text-center tabular-nums whitespace-nowrap bg-white dark:bg-darkmode-600 text-[11px] font-bold border-r border-slate-200 dark:border-darkmode-400">{formatIntOrDash(typeDistributorByReceipt[i] ?? 0)}</Table.Td>,
                                        <Table.Td key={`ex-total-${r.id}`} style={colStyle(COL_RECEIPT_EXCHANGE_PX)} className="py-2 px-2 text-center tabular-nums whitespace-nowrap bg-white dark:bg-darkmode-600 text-[11px] font-bold border-r border-slate-200 dark:border-darkmode-400">{formatIntOrDash(typeExchangeByReceipt[i] ?? 0)}</Table.Td>,
                                      ];
                                    }),
                                  ])}
                                  <Table.Td style={colStyle(COL_TOTAL_PX)} className="py-2 px-2 text-center tabular-nums whitespace-nowrap bg-white dark:bg-darkmode-600 text-[11px] font-bold border-r border-slate-200 dark:border-darkmode-400">{formatIntOrDash(typeGrandTotal)}</Table.Td>
                                </Table.Tr>
                              </Table.Tbody>
                            </Table>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </RoleGate>
    </ProtectedRoute>
  );
}
